import { z } from 'zod';
import { fieldValueId } from '../ids.js';
import { blockRowSchema, fieldValueRowSchema, instantSchema, uuidSchema } from '../schema/rows.js';
import { capturedTagInput, tagWrites } from './capture.js';
import { DomainError } from './types.js';
/**
 * Follow-up fields of a task (SEG-01, docs/04 §5): «Esperando a» (a person tag in `value_ref`) and
 * «Fecha de seguimiento» (`value_date`). The triage writes them with `S` and the detail panel
 * changes them later; both go through the same writes, with deterministic `field_values` ids and
 * the undo that restores what the block had.
 */
export const fieldValueSnapshot = fieldValueRowSchema.pick({
    id: true,
    workspace_id: true,
    block_id: true,
    field_id: true,
    value_text: true,
    value_number: true,
    value_date: true,
    value_ts: true,
    value_bool: true,
    value_ref: true,
    value_json: true,
    created_at: true,
});
/** The two `field_values` rows of a follow-up, with their undo. */
export function followUpWrites(input) {
    const operations = [];
    const undo = [];
    const values = [
        [input.fields.follow_up_on, { value_date: input.follow_up_on }],
        [input.fields.waiting_on, { value_ref: input.person_tag_id }],
    ];
    for (const [fieldId, value] of values) {
        const id = fieldValueId(input.block_id, fieldId);
        operations.push({
            table: 'field_values',
            op: 'PUT',
            id,
            data: {
                workspace_id: input.workspace_id,
                block_id: input.block_id,
                field_id: fieldId,
                value_text: null,
                value_number: null,
                value_date: null,
                value_ts: null,
                value_bool: null,
                value_ref: null,
                value_json: null,
                created_at: input.now,
                ...value,
            },
        });
        const before = input.previous.find((row) => row.field_id === fieldId);
        if (before) {
            const { id: previousId, ...data } = before;
            undo.push({ table: 'field_values', op: 'PUT', id: previousId, data });
        }
        else {
            undo.push({ table: 'field_values', op: 'DELETE', id });
        }
    }
    return { operations, undo };
}
const setFollowUpInput = z.object({
    block: z.object({ id: uuidSchema, workspace_id: uuidSchema }),
    /** Person waited on: an existing tag or a name to create. */
    waiting_on: capturedTagInput,
    follow_up_on: blockRowSchema.shape.due_on.unwrap(),
    fields: z.object({ waiting_on: uuidSchema, follow_up_on: uuidSchema }),
    previous: z.array(fieldValueSnapshot).default([]),
    now: instantSchema.optional(),
});
/** Changes «Esperando a» and «Fecha de seguimiento» from the detail panel (SEG-01). */
export function setFollowUp(input) {
    const parsed = setFollowUpInput.parse(input);
    const now = parsed.now ?? new Date().toISOString();
    const person = tagWrites(parsed.block.workspace_id, [parsed.waiting_on], now);
    const leaf = person.leaves[0];
    if (!leaf)
        throw new DomainError('missing_field', 'Indica a quién esperas.', 'SEG-01');
    const fields = followUpWrites({
        workspace_id: parsed.block.workspace_id,
        block_id: parsed.block.id,
        fields: parsed.fields,
        person_tag_id: leaf.tagId,
        follow_up_on: parsed.follow_up_on,
        previous: parsed.previous,
        now,
    });
    return {
        operations: [...person.tags, ...fields.operations],
        undo: fields.undo,
    };
}
//# sourceMappingURL=follow-up.js.map